import React, { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';

interface PortalSwitcherProps {
  currentPortal?: string;
}

const PortalSwitcher: React.FC<PortalSwitcherProps> = ({ currentPortal = 'onboarding' }) => {
  const [switching, setSwitching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const portals = [
    {
      id: 'hrms',
      name: 'HRMS',
      icon: '/favicon.ico',
      url: import.meta.env.VITE_HRMS_URL || 'http://localhost:3000',
      active: currentPortal === 'hrms'
    },
    {
      id: 'onboarding',
      name: 'Onboarding',
      icon: '/favicon.ico',
      url: import.meta.env.VITE_ONBOARDING_URL || 'http://localhost:3001',
      active: currentPortal === 'onboarding'
    }
  ];

  const currentPortalData = portals.find(p => p.active) || portals[1];

  const handlePortalSwitch = async (portal: typeof portals[0]) => {
    if (portal.active) return;

    setSwitching(true);
    
    try {
      // Get current auth token to pass to other portal
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Construct URL with token for seamless authentication
      const targetUrl = new URL(portal.url);
      if (token) {
        targetUrl.searchParams.set('auth_token', token);
        targetUrl.searchParams.set('portal_switch', 'true');
      }
      
      // Switch to the other portal in the same tab
      window.location.href = targetUrl.toString();
    } catch (error) {
      console.error('Error switching portals:', error);
      // Fallback: open in new tab if same-tab switch fails
      window.open(portal.url, '_blank');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <DropdownMenu.Root onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <button className="flex items-center w-full text-left hover:bg-gray-50 rounded-lg p-2 transition-colors duration-200 outline-none group">
          <div className="p-2 mr-3 flex-shrink-0">
            <img 
              src={currentPortalData.icon} 
              alt={`${currentPortalData.name} icon`}
              className="w-6 h-6" 
            />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-gray-900 truncate">
              {currentPortalData.name}
            </h1>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-all duration-200 flex-shrink-0 ml-2 ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`} />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content 
          className="w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 p-1"
          sideOffset={8}
          align="start"
        >
          {portals.map((portal) => {
            return (
              <DropdownMenu.Item
                key={portal.id}
                className={`${
                  portal.active ? 'bg-blue-50' : ''
                } flex items-center w-full px-3 py-2 text-left rounded-md transition-colors duration-200 ${
                  portal.active ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'
                } ${switching ? 'opacity-50 cursor-not-allowed' : ''} outline-none`}
                disabled={portal.active || switching}
                onSelect={() => !portal.active && !switching && handlePortalSwitch(portal)}
              >
                <div className="p-1 mr-2 flex-shrink-0">
                  <img 
                    src={portal.icon} 
                    alt={`${portal.name} icon`}
                    className="w-4 h-4" 
                  />
                </div>
                <span className={`text-sm font-medium ${
                  portal.active ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {portal.name}
                </span>
                {portal.active && (
                  <span className="ml-auto text-xs text-blue-600">•</span>
                )}
              </DropdownMenu.Item>
            );
          })}
          
          {switching && (
            <div className="px-3 py-2 border-t border-gray-100 mt-1">
              <div className="flex items-center text-xs text-gray-600">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
                Switching...
              </div>
            </div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default PortalSwitcher;